import { z, ZodError } from 'zod/v3';
import {
  decodeStringKey,
  errorMap,
  getMapBy,
  isSafeFiniteInteger,
  zCodeWithoutLeadingNumber,
} from '../../../../common/utils';
import { zUniqueKey } from '../../../../common/utils/schema/zUniqueKey';
import { SystemClassifierItemSchema } from '../../../../systemClassifier';
import {
  refineValue,
  validateValueFormat,
} from '../../joins/schema/validFormData/AlgorithmJoinItemValidFormDataSchema';
import { isDimensionWithCodeFormat } from '../../joins/utils';
import { AlgorithmMappingValidFormDataSchema } from '../../mappings/schema/AlgorithmMappingValidFormDataSchema';
import { AlgorithmSourceFormDataSchema } from '../../sources/schema/AlgorithmSourceFormDataSchema';
import {
  type AlgorithmJoinHandleId,
  decodeDimensionCodeValue,
  getHandleId,
  parseHandleId,
} from '../utils';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinsAndMappingsGraphValidFormDataSchema.translations.json';
import { resolveOwnerNode } from './utils';

const { addTranslations, t } = i18nNsFactory(
  'algorithm-joins-and-mappings-graph-valid-form-data-schema',
);
addTranslations(translations);

const InitialPositionSchema = z.object({
  x: z.number().default(0),
  y: z.number().default(0),
});

export const AlgorithmJoinsAndMappingsGraphNodeSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      id: zUniqueKey(),
      type: z.literal('algorithmSource'),
      position: InitialPositionSchema,
      data: z.object({
        id: z.number().optional(),
        algorithmSourceId: z
          .number(errorMap(() => t('validation.algorithmSourceId.required')))
          .min(1),
        joinTypeCode: z.enum(['FULL_OUTER', 'LEFT_OUTER', 'INNER']).nullable(),
        items: z.array(
          z.union([
            z.object({
              id: z.number().optional(),
              dimensionCode: z.string().superRefine((value, ctx) => {
                const { dimensionCode } = decodeDimensionCodeValue(value);
                try {
                  zCodeWithoutLeadingNumber().parse(dimensionCode);
                } catch (error) {
                  if (error instanceof ZodError) {
                    error.issues.forEach(ctx.addIssue);
                  }
                }
              }),
            }),
            z.object({
              id: z.number().optional(),
              constantValue: z
                .string(errorMap(() => t('validation.constantValue.required')))
                .min(1),
            }),
          ]),
        ),
      }),
    }),
    z.object({
      id: zUniqueKey(),
      type: z.literal('algorithmMapping'),
      position: InitialPositionSchema,
      data: z.object({
        algorithmMapping: AlgorithmMappingValidFormDataSchema,
      }),
    }),
    z.object({
      id: zUniqueKey(),
      type: z.literal('constant'),
      position: InitialPositionSchema,
      data: z.object({
        constantValue: z
          .string(errorMap(() => t('validation.constantValue.required')))
          .min(1),
      }),
    }),
  ],
);

const HandleSchema = z.custom<AlgorithmJoinHandleId>(
  (value): value is AlgorithmJoinHandleId => {
    if (typeof value !== 'string') {
      return false;
    }
    const parts = decodeStringKey(value);
    return (
      parts.length === 4 &&
      parts[0].split('-').length === 5 && // basic UUID v4 check
      isSafeFiniteInteger(parts[1]) && // item index
      (parts[2] === 'source' || parts[2] === 'target') &&
      parts[3] === 'handle'
    );
  },
);

export const AlgorithmJoinsAndMappingsGraphEdgeSchema = z.object({
  id: zUniqueKey(),
  source: zUniqueKey(),
  sourceHandle: HandleSchema,
  target: zUniqueKey(),
  targetHandle: HandleSchema,
  data: z
    .object({
      // the database ID of the join item (for stable updates on roundtrip)
      itemId: z.number().optional(),
      // the database ID of the algorithmSource join that owns this item (for stable updates on roundtrip)
      joinNodeId: zUniqueKey().optional(),
    })
    .optional(),
});

export const AlgorithmJoinsAndMappingsGraphValidFormDataSchema = z
  .object({
    nodes: z.array(AlgorithmJoinsAndMappingsGraphNodeSchema),
    edges: z.array(AlgorithmJoinsAndMappingsGraphEdgeSchema),
    // Algorithm sources and classifier data types are included only
    // for validation purposes, they are not edited in the form.
    sources: z.array(AlgorithmSourceFormDataSchema),
    classifierDataTypesById: z.map(z.number(), SystemClassifierItemSchema),
  })
  /*
   * Check node count: at least two algorithm source nodes (or none) is allowed
   */
  .superRefine(({ nodes }, ctx) => {
    if (nodes.filter((node) => node.type === 'algorithmSource').length === 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.minimum-2-joins-required'),
        path: ['nodes'],
      });
    }
  })
  /*
   * Check value formats of those constants that are connected to
   * dimensions of classifiers with category format
   */
  .superRefine(
    async ({ nodes, edges, sources, classifierDataTypesById }, ctx) => {
      const algorithmSourcesById = getMapBy(sources, 'id');
      const nodesById = getMapBy(nodes, 'id');
      const edgesBySourceNodeId = edges.reduce(
        (map, edge) =>
          map.set(edge.source, (map.get(edge.source) ?? []).concat(edge)),
        new Map<string, typeof edges>(),
      );
      const edgesByTargetHandleId = edges.reduce(
        (map, edge) =>
          map.set(
            edge.targetHandle,
            (map.get(edge.targetHandle) ?? []).concat(edge),
          ),
        new Map<string, typeof edges>(),
      );

      const promises: Promise<void>[] = [];

      const validateConstantValueAgainstConnectedItemCategoryFormat = (
        constantValue: string,
        handleId: AlgorithmJoinHandleId,
        path: (string | number)[],
        role: 'source' | 'target',
      ) => {
        const { nodeId, itemIndex, type } = parseHandleId(handleId);
        const node = nodesById.get(nodeId);

        if (node?.type === 'algorithmSource') {
          const { algorithmSourceId, items } = node.data;
          const connectedItem = items[itemIndex];

          if (connectedItem && 'dimensionCode' in connectedItem) {
            const algorithmSource = algorithmSourcesById.get(algorithmSourceId);
            const { dimensionCode } = decodeDimensionCodeValue(
              connectedItem.dimensionCode,
            );
            const dimension = algorithmSource?.dimensions.find(
              (d) => d.code === dimensionCode,
            );

            if (isDimensionWithCodeFormat(dimension)) {
              const dataType = classifierDataTypesById.get(
                dimension.classifier.dataTypeId,
              );
              if (!dataType) {
                throw new Error(
                  `System classifier item of CLASSIFIER_DATA_TYPE with ID ${dimension.classifier.dataTypeId} not found`,
                );
              }
              promises.push(
                validateValueFormat(
                  type,
                  constantValue,
                  dimension.classifier.codeFormat,
                  dataType.code,
                  (message: string) => {
                    ctx.addIssue({ code: 'custom', message, path });
                  },
                ),
              );
            } else {
              refineValue(role, ctx, constantValue);
            }
          }
        }
      };

      nodes.forEach((node, nodeIndex) => {
        if (node.type === 'constant') {
          const targetEdges = edgesBySourceNodeId.get(node.id);
          if (targetEdges?.length) {
            validateConstantValueAgainstConnectedItemCategoryFormat(
              node.data.constantValue,
              targetEdges[0].targetHandle,
              ['nodes', nodeIndex, 'data', 'constantValue'],
              'source',
            );
          }
        } else if (node.type === 'algorithmSource') {
          node.data.items.forEach((item, itemIndex) => {
            if ('constantValue' in item) {
              const sourceEdges = edgesByTargetHandleId.get(
                getHandleId(node.id, itemIndex, 'target'),
              );
              if (sourceEdges?.length) {
                validateConstantValueAgainstConnectedItemCategoryFormat(
                  item.constantValue,
                  sourceEdges[0].sourceHandle,
                  [
                    'nodes',
                    nodeIndex,
                    'data',
                    'items',
                    itemIndex,
                    'constantValue',
                  ],
                  'target',
                );
              }
            }
          });
        }
      });

      return Promise.allSettled(promises);
    },
  )
  /*
   * Check for dangling nodes, node items, and mapping edges with no resolvable owner join
   */
  .superRefine(({ nodes, edges }, ctx) => {
    const nodesById = getMapBy(nodes, 'id');
    const usedAlgorithmMappingAliases = new Set<string>();
    const seenAlgorithmMappingNodeIds = new Set<string>();

    const algorithmSourceNodes = nodes.filter(
      (n): n is Extract<(typeof nodes)[number], { type: 'algorithmSource' }> =>
        n.type === 'algorithmSource',
    );

    const markUsed = (object: any) => (object._isUsed = true);
    const isUnused = (object: any) => object._isUsed !== true;

    const joinsWithItems = new Set<string>();

    edges.forEach((edge) => {
      const sourceNode = nodesById.get(edge.source)!;
      const targetNode = nodesById.get(edge.target)!;

      if (sourceNode.type === 'constant') {
        markUsed(sourceNode.data);
      } else if (sourceNode.type === 'algorithmMapping') {
        if (!seenAlgorithmMappingNodeIds.has(sourceNode.id)) {
          seenAlgorithmMappingNodeIds.add(sourceNode.id);
          if (
            usedAlgorithmMappingAliases.has(
              sourceNode.data.algorithmMapping.alias,
            )
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.algorithmMapping.alias.duplicate', {
                alias: sourceNode.data.algorithmMapping.alias,
              }),
              path: [
                'nodes',
                nodes.indexOf(sourceNode),
                'data',
                'algorithmMapping',
                'alias',
              ],
            });
          } else {
            usedAlgorithmMappingAliases.add(
              sourceNode.data.algorithmMapping.alias,
            );
          }
        }
      } else if (sourceNode.type === 'algorithmSource') {
        const { itemIndex: sourceItemIndex } = parseHandleId(edge.sourceHandle);
        markUsed(sourceNode.data.items[sourceItemIndex]);
      }
      if (targetNode.type === 'algorithmSource') {
        const { itemIndex: targetItemIndex } = parseHandleId(edge.targetHandle);
        markUsed(targetNode.data.items[targetItemIndex]);
        joinsWithItems.add(targetNode.id);
      }

      // Validate whether DIMENSION_MAPPING/VALUE_MAPPING join item can be
      // appropriately saved under any join
      if (targetNode.type === 'algorithmMapping') {
        const isSourceDimension = sourceNode.type === 'algorithmSource';

        const ownerNode = resolveOwnerNode({
          edgeSourceNode: sourceNode,
          originalOwnerNode:
            edge.data?.joinNodeId && nodesById.get(edge.data.joinNodeId),
          allAlgorithmSourceNodes: algorithmSourceNodes,
        });

        if (ownerNode) {
          joinsWithItems.add(ownerNode.id);
        } else {
          if (algorithmSourceNodes.length < 2) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.minimum-2-joins-required'),
              path: ['nodes'],
            });
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.unable-to-resolve-owner-join'),
              path: [
                'nodes',
                nodes.indexOf(sourceNode),
                'data',
                'items',
                isSourceDimension
                  ? parseHandleId(edge.sourceHandle).itemIndex
                  : 0,
                isSourceDimension ? 'dimensionCode' : 'constantValue',
              ],
            });
          }
        }
      }
    });

    if (nodesById.size === 0) {
      return;
    }

    if (algorithmSourceNodes.length >= 2) {
      algorithmSourceNodes.forEach((node, sourceIndex) => {
        if (sourceIndex > 0 && !joinsWithItems.has(node.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.join-items-required'),
            path: ['nodes', nodes.indexOf(node), 'data', 'algorithmSourceId'],
          });
        }
      });
    }

    Array.from(nodesById.values()).forEach((node, index) => {
      if (node.type === 'constant' && isUnused(node.data)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation.constantValue.unused'),
          path: ['nodes', index, 'data', 'constantValue'],
        });
      } else if (node.type === 'algorithmSource') {
        node.data.items.forEach((item, itemIndex) => {
          if (isUnused(item)) {
            const fieldName =
              'dimensionCode' in item ? 'dimensionCode' : 'constantValue';
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(`validation.${fieldName}.unused`),
              path: ['nodes', index, 'data', 'items', itemIndex, fieldName],
            });
          }
        });
      }
    });
  });
